import React, { useEffect, useMemo, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../../lib/api";

const TZ = "Europe/London";
const ymd = (d) => d.toLocaleDateString("en-CA", { timeZone: TZ });
const addDays = (s, n) => { const d = new Date(`${s}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return ymd(d); };
const dayLabel = (s) => new Date(`${s}T12:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
const timeLabel = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: TZ });

const DayColumn = ({ day, value, onChange }) => (
  <div data-testid={`slot-day-${day.key}`}>
    <div className="text-xs uppercase tracking-[0.12em] text-ink-muted pb-2 border-b border-stone-200/70">{dayLabel(day.key)}</div>
    <div className="mt-2 flex flex-col gap-1.5">
      {day.slots.length === 0 && <span className="text-xs text-ink-muted/70 py-1">No times</span>}
      {day.slots.map((iso) => (
        <button key={iso} type="button" onClick={() => onChange(iso)} data-testid={`slot-${iso}`}
          className={`text-sm py-1.5 rounded-sm border transition-colors ${value === iso ? "bg-bronze-dark text-white border-bronze-dark ring-2 ring-bronze-dark ring-offset-1 font-semibold" : "border-bronze-light/60 text-bronze-dark hover:bg-cream-100"}`}>
          {timeLabel(iso)}
        </button>
      ))}
    </div>
  </div>
);

const WeekNav = ({ start, today, setStart }) => (
  <div className="flex items-center justify-between">
    <button type="button" onClick={() => setStart(addDays(start, -7))} disabled={start <= today}
      data-testid="slot-prev-week" className="p-2 rounded-full hover:bg-cream-100 disabled:opacity-30" aria-label="Previous week"><CaretLeft size={18} /></button>
    <div className="text-sm font-medium" data-testid="slot-week-label">{dayLabel(start)} – {dayLabel(addDays(start, 6))}</div>
    <button type="button" onClick={() => setStart(addDays(start, 7))} disabled={start >= addDays(today, 105)}
      data-testid="slot-next-week" className="p-2 rounded-full hover:bg-cream-100 disabled:opacity-30" aria-label="Next week"><CaretRight size={18} /></button>
  </div>
);

const NoSlots = ({ next, setStart }) => (
  <p data-testid="slot-none" className="mt-4 text-sm text-ink-muted">
    No free times this week.{" "}
    {next ? <button type="button" data-testid="slot-jump-next" onClick={() => setStart(ymd(new Date(next)))} className="text-bronze-dark underline underline-offset-4">Jump to the next available date ({fmtSlot(next)})</button>
      : "Please try a later week or contact us."}
  </p>
);

export const SlotPicker = ({ value, onChange, refreshKey, consultant = "any", initialStart, tests = [], visit = "initial_consultation_gbp", step = 3, anyTests = false, holterDays = 1 }) => {
  const testsKey = tests.join(",");
  const today = addDays(ymd(new Date()), 7);
  const first = initialStart && initialStart > today ? initialStart : today;
  const [start, setStart] = useState(first);
  const [next, setNext] = useState(null);

  useEffect(() => { setStart(first); }, [consultant, first, testsKey]);
  const [slots, setSlots] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setSlots(null); setError(""); setNext(null);
    api.get("/booking/slots", { params: { start, consultant, tests: testsKey, visit, holter_days: holterDays, ...(anyTests ? { any_tests: true } : {}) } })
      .then(({ data }) => { if (live) { setSlots(data.slots); setNext(data.next); } })
      .catch((e) => live && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    return () => { live = false; };
  }, [start, refreshKey, consultant, testsKey, visit, anyTests, holterDays]);

  const days = useMemo(() => {
    const byDay = {};
    (slots || []).forEach((iso) => { const k = ymd(new Date(iso)); (byDay[k] = byDay[k] || []).push(iso); });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i)).map((k) => ({ key: k, slots: byDay[k] || [] }));
  }, [slots, start]);

  return (
    <div data-testid="slot-picker" className="bg-white border border-bronze-light/30 rounded-md p-5 sm:p-6">
      <div className="eyebrow">Step {step}</div>
      <h2 className="font-serif text-2xl mt-1">Choose a time</h2>
      <p className="text-sm text-ink-muted mt-1 mb-5">Times are UK time and show when to arrive{tests.length && visit !== "tests_only" ? " - your tests come first with our nurse or echo physiologist, then your consultation" : ""}. Appointments can be booked from 7 days ahead.</p>
      <WeekNav start={start} today={today} setStart={setStart} />
      {error && <div data-testid="slot-error" className="mt-6 text-sm text-destructive">{error}</div>}
      {!slots && !error && <div className="mt-6 text-sm text-ink-muted">Loading available times…</div>}
      {slots && (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {days.map((d) => <DayColumn key={d.key} day={d} value={value} onChange={onChange} />)}
        </div>
      )}
      {slots && slots.length === 0 && <NoSlots next={next} setStart={setStart} />}
    </div>
  );
};

export const fmtSlot = (iso) => new Date(iso).toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: TZ });
